import { IGetOneResponse } from '../util/Types/Generic';
import { ApiService } from './ApiService';

export interface Library {
  id: string;
  type: 'system' | 'organization' | 'space';
  company_id: string;
  space_id: string | null;
  show_data: boolean;
  show_file: boolean;
  image_url: string | null;
  visible_input_ids: string[] | null;
  visible_output_ids: string[] | null;
  flow: any | null;
  created_at: string;
  updated_at: string;
}

export class LibraryService extends ApiService {
  static pathName = '/library';

  static createSystemLibrary = (
    companyId: string | null,
    flowId: string,
    options?: {
      image?: File;
      visible_input_ids?: string[];
      visible_output_ids?: string[];
    },
  ): Promise<IGetOneResponse<Library>> => {
    const { image, visible_input_ids, visible_output_ids } = options || {};

    if (image || visible_input_ids || visible_output_ids) {
      const formData = new FormData();
      if (companyId) {
        formData.append('company_id', companyId);
      }
      formData.append('flow_id', flowId);
      if (image) {
        formData.append('image', image);
      }
      if (visible_input_ids) {
        formData.append('visible_input_ids', JSON.stringify(visible_input_ids));
      }
      if (visible_output_ids) {
        formData.append('visible_output_ids', JSON.stringify(visible_output_ids));
      }
      return ApiService.post(`${this.pathName}/system`, formData);
    }

    return ApiService.post(`${this.pathName}/system`, {
      ...(companyId && { company_id: companyId }),
      flow_id: flowId,
    });
  };

  static createOrganizationLibrary = (
    companyId: string,
    flowId: string,
    options?: {
      image?: File;
      visible_input_ids?: string[];
      visible_output_ids?: string[];
    },
  ): Promise<IGetOneResponse<Library>> => {
    const { image, visible_input_ids, visible_output_ids } = options || {};

    if (image || visible_input_ids || visible_output_ids) {
      const formData = new FormData();
      formData.append('company_id', companyId);
      formData.append('flow_id', flowId);
      if (image) {
        formData.append('image', image);
      }
      if (visible_input_ids) {
        formData.append('visible_input_ids', JSON.stringify(visible_input_ids));
      }
      if (visible_output_ids) {
        formData.append('visible_output_ids', JSON.stringify(visible_output_ids));
      }
      return ApiService.post(`${this.pathName}/organization`, formData);
    }

    return ApiService.post(`${this.pathName}/organization`, {
      company_id: companyId,
      flow_id: flowId,
    });
  };

  static createSpaceLibrary = (
    companyId: string,
    spaceId: string | null | undefined,
    flowId: string,
    options?: {
      image?: File;
      visible_input_ids?: string[];
      visible_output_ids?: string[];
    },
  ): Promise<IGetOneResponse<Library>> => {
    const { image, visible_input_ids, visible_output_ids } = options || {};

    if (image || visible_input_ids || visible_output_ids) {
      const formData = new FormData();
      formData.append('company_id', companyId);
      if (spaceId) {
        formData.append('space_id', spaceId);
      }
      formData.append('flow_id', flowId);
      if (image) {
        formData.append('image', image);
      }
      if (visible_input_ids) {
        formData.append('visible_input_ids', JSON.stringify(visible_input_ids));
      }
      if (visible_output_ids) {
        formData.append('visible_output_ids', JSON.stringify(visible_output_ids));
      }
      return ApiService.post(`${this.pathName}/space`, formData);
    }

    return ApiService.post(`${this.pathName}/space`, {
      company_id: companyId,
      ...(spaceId && { space_id: spaceId }),
      flow_id: flowId,
    });
  };

  static getSystemLibraries = (
    name?: string,
  ): Promise<Library[]> => {
    const params = name ? { name } : undefined;
    return ApiService.get(`${this.pathName}/system/list`, undefined, undefined, params);
  };

  static getOrganizationLibraries = (
    name?: string,
  ): Promise<Library[]> => {
    const params = name ? { name } : undefined;
    return ApiService.get(`${this.pathName}/organization/list`, undefined, undefined, params);
  };

  static getSpaceLibraries = (
    spaceId: string,
    name?: string,
  ): Promise<Library[]> => {
    const params: Record<string, string> = { space_id: spaceId };
    if (name) {
      params.name = name;
    }
    return ApiService.get(`${this.pathName}/space/list`, undefined, undefined, params);
  };

  static createPrivateLibrary = (
    companyId: string,
    flowId: string,
    options?: {
      image?: File;
      visible_input_ids?: string[];
      visible_output_ids?: string[];
    },
  ): Promise<IGetOneResponse<Library>> => {
    const { image, visible_input_ids, visible_output_ids } = options || {};

    if (image || visible_input_ids || visible_output_ids) {
      const formData = new FormData();
      formData.append('company_id', companyId);
      formData.append('flow_id', flowId);
      if (image) {
        formData.append('image', image);
      }
      if (visible_input_ids) {
        formData.append('visible_input_ids', JSON.stringify(visible_input_ids));
      }
      if (visible_output_ids) {
        formData.append('visible_output_ids', JSON.stringify(visible_output_ids));
      }
      return ApiService.post(`${this.pathName}/private`, formData);
    }

    return ApiService.post(`${this.pathName}/private`, {
      company_id: companyId,
      flow_id: flowId,
    });
  };

  static getPrivateLibraries = (
    name?: string,
  ): Promise<Library[]> => {
    const params = name ? { name } : undefined;
    return ApiService.get(`${this.pathName}/private/list`, undefined, undefined, params);
  };

  static updateLibrary = (
    libraryId: string,
    options?: {
      image?: File;
      type?: 'system' | 'organization' | 'space' | 'private';
      company_id?: string;
      space_id?: string | null;
      visible_input_ids?: string[];
      visible_output_ids?: string[];
    },
  ): Promise<IGetOneResponse<Library>> => {
    const { image, type, company_id, space_id, visible_input_ids, visible_output_ids } = options || {};

    const formData = new FormData();
    formData.append('library_id', libraryId);

    if (type !== undefined) {
      formData.append('type', type);
    }

    if (company_id !== undefined) {
      formData.append('company_id', company_id || '');
    }

    if (space_id !== undefined) {
      formData.append('space_id', space_id || '');
    }

    if (visible_input_ids !== undefined) {
      formData.append('visible_input_ids', JSON.stringify(visible_input_ids));
    }

    if (visible_output_ids !== undefined) {
      formData.append('visible_output_ids', JSON.stringify(visible_output_ids));
    }

    if (image) {
      formData.append('image', image);
    }

    return ApiService.patch(`${this.pathName}/update`, formData);
  };

  static deleteLibrary = (libraryId: string) => {
    return ApiService.delete(`${this.pathName}/delete/${libraryId}`);
  };
}
